import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  adminHistoryDeposits,
  adminHistoryRedemptions,
  adminHistoryTransactions,
  adminHistoryTransfers,
  adminHistoryPlatformFees,
  adminHistoryWithdrawals,
  downloadHistoryPdf,
  investorHistoryDeposits,
  investorHistoryRedemptions,
  investorHistoryTransactions,
  investorHistoryTransfers,
  investorHistoryWithdrawals,
  requestAdminArchiveReport,
} from "../services/historyService";
import { fetchPortfolio, fetchRedemptionBalance, listProjects, unwrapListPayload } from "../services/investmentService";
import HistoryTabLabel from "../components/investor/HistoryTabLabel";
import InvestorPageHeader from "../components/investor/InvestorPageHeader";
import MyPortfolioSection from "../components/investor/MyPortfolioSection";
import {
  ADMIN_HISTORY_SECTIONS,
  HISTORY_SECTIONS,
  INVESTOR_HOLDINGS_SECTION,
  MOVEMENT_OPTIONS,
  SECTION_META,
  SUMMARY_CARD_LABELS,
  getColumnsForSection,
  getInvestorHistoryTabItems,
  INVESTOR_HISTORY_PAGE_TITLE,
  investorHistorySections,
  normalizeHistorySection,
} from "../utils/historyModuleConfig";
import { investorLifecyclePillClass } from "../utils/investorLifecyclePill";

const UI_FALLBACK_DAYS = 730;

const fetchers = {
  deposits: { admin: adminHistoryDeposits, investor: investorHistoryDeposits },
  withdrawals: { admin: adminHistoryWithdrawals, investor: investorHistoryWithdrawals },
  transactions: { admin: adminHistoryTransactions, investor: investorHistoryTransactions },
  redemptions: { admin: adminHistoryRedemptions, investor: investorHistoryRedemptions },
  transfers: { admin: adminHistoryTransfers, investor: investorHistoryTransfers },
  "platform-fees": { admin: adminHistoryPlatformFees, investor: null },
};

function statusBadgeClass(s) {
  const v = String(s || "").toUpperCase();
  if (["APPROVED", "COMPLETED", "ACTIVE"].includes(v)) return "history-badge history-badge--success";
  if (["PENDING", "PROCESSING", "AWAITING_RECEIVER", "LOCK_IN"].includes(v)) return "history-badge history-badge--pending";
  if (["REJECTED", "FAILED", "CANCELLED", "SUSPENDED"].includes(v)) return "history-badge history-badge--danger";
  return "history-badge history-badge--neutral";
}

function rowMatchesQuickSearch(row, q) {
  if (!q || !q.trim()) return true;
  const needle = q.trim().toLowerCase();
  return Object.values(row).some((v) => String(v ?? "").toLowerCase().includes(needle));
}

function SummaryCards({ summary }) {
  if (!summary || typeof summary !== "object") return null;
  const entries = Object.entries(summary);
  if (!entries.length) return null;
  return (
    <div className="history-summary-grid">
      {entries.map(([key, value]) => (
        <div key={key} className="history-summary-card">
          <div className="history-summary-card__label">{SUMMARY_CARD_LABELS[key] || key.replace(/_/g, " ")}</div>
          <div className="history-summary-card__value">{String(value)}</div>
        </div>
      ))}
    </div>
  );
}

export default function FinancialHistoryPage({ isAdmin }) {
  const { section: sectionParam } = useParams();
  const navigate = useNavigate();
  const basePath = isAdmin ? "/admin/history" : "/my-history";
  const historySections = isAdmin ? ADMIN_HISTORY_SECTIONS : investorHistorySections();
  const rawSection = String(sectionParam || "").toLowerCase();
  const section = normalizeHistorySection(rawSection, { isAdmin });
  const isHoldingsView = !isAdmin && section === INVESTOR_HOLDINGS_SECTION;

  useEffect(() => {
    const raw = String(sectionParam || "").toLowerCase();
    if (sectionParam != null && sectionParam !== "" && !historySections.includes(raw)) {
      navigate(`${basePath}/deposits`, { replace: true });
    }
  }, [sectionParam, navigate, basePath, historySections]);

  const meta = SECTION_META[section];
  const { keys: columnKeys, labels: headerLabelsMap } = getColumnsForSection(section, { isAdmin });

  const [projects, setProjects] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [quickSearch, setQuickSearch] = useState("");
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    status: "",
    project_id: "",
    investor_id: "",
    movement_type: "",
    txn_type: "",
    archive: false,
    page: 1,
    page_size: 25,
  });
  const [archiveForm, setArchiveForm] = useState({ date_from: "", date_to: "", recipient_email: "" });
  const [archiveMsg, setArchiveMsg] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [portfolio, setPortfolio] = useState([]);
  const [redemptionBalances, setRedemptionBalances] = useState([]);
  const [holdingsLoading, setHoldingsLoading] = useState(false);

  const bdtFull = useMemo(
    () => new Intl.NumberFormat("en-BD", { style: "currency", currency: "BDT", maximumFractionDigits: 2 }),
    []
  );
  const shareFmt = useMemo(
    () => new Intl.NumberFormat("en-BD", { maximumFractionDigits: 2, minimumFractionDigits: 0 }),
    []
  );
  const formatShare = (n) => shareFmt.format(Number(n || 0));

  const loadHoldings = useCallback(async () => {
    setHoldingsLoading(true);
    try {
      const [portfolioRes, balRes] = await Promise.all([
        fetchPortfolio(),
        fetchRedemptionBalance().catch(() => ({ data: null })),
      ]);
      setPortfolio(unwrapListPayload(portfolioRes.data));
      const bal = balRes?.data;
      setRedemptionBalances(Array.isArray(bal?.balances) ? bal.balances : bal?.project_id ? [bal] : []);
    } catch {
      setPortfolio([]);
      setRedemptionBalances([]);
    } finally {
      setHoldingsLoading(false);
    }
  }, []);

  useEffect(() => {
    listProjects()
      .then((res) => setProjects(Array.isArray(res.data) ? res.data : res.data?.results || []))
      .catch(() => setProjects([]));
  }, []);

  useEffect(() => {
    setQuickSearch("");
    setFilters((f) => ({
      ...f,
      page: 1,
      movement_type: section === "transactions" ? f.movement_type : "",
      status: "",
    }));
  }, [section]);

  useEffect(() => {
    if (!isHoldingsView) return undefined;
    loadHoldings().catch(() => {});
    return undefined;
  }, [isHoldingsView, loadHoldings]);

  const load = useCallback(async () => {
    if (isHoldingsView) return;
    setLoading(true);
    setError("");
    try {
      const fn = isAdmin ? fetchers[section].admin : fetchers[section].investor;
      if (!fn) {
        setData(null);
        setError("This section is only available to administrators.");
        return;
      }
      const payload = {
        page: filters.page,
        page_size: filters.page_size,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        status: filters.status || undefined,
        project_id: filters.project_id || undefined,
        investor_id: isAdmin ? filters.investor_id || undefined : undefined,
        movement_type: filters.movement_type || undefined,
        fee_type: section === "platform-fees" ? filters.status || undefined : undefined,
        txn_type: filters.txn_type || undefined,
        archive: isAdmin && filters.archive,
      };
      const res = await fn(payload);
      setData(res.data);
    } catch (e) {
      setData(null);
      setError(e.response?.data?.detail || e.message || "Failed to load history.");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, section, filters, isHoldingsView]);

  useEffect(() => {
    if (isHoldingsView) return undefined;
    load().catch(() => {});
    return undefined;
  }, [load, isHoldingsView]);

  const onPdf = async () => {
    setPdfBusy(true);
    setError("");
    try {
      const payload = {
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        status: filters.status || undefined,
        project_id: filters.project_id || undefined,
        investor_id: isAdmin ? filters.investor_id || undefined : undefined,
        movement_type: filters.movement_type || undefined,
        txn_type: filters.txn_type || undefined,
        archive: isAdmin && filters.archive,
      };
      const res = await downloadHistoryPdf(isAdmin, section, payload);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${section}_history.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "PDF export failed.");
    } finally {
      setPdfBusy(false);
    }
  };

  const submitArchive = async (e) => {
    e.preventDefault();
    setArchiveMsg("");
    if (!isAdmin) return;
    const moduleMap = {
      deposits: "DEPOSITS",
      withdrawals: "WITHDRAWALS",
      transactions: "TRANSACTIONS",
      redemptions: "REDEMPTIONS",
      transfers: "TRANSFERS",
    };
    try {
      await requestAdminArchiveReport({
        module: moduleMap[section],
        date_from: archiveForm.date_from,
        date_to: archiveForm.date_to,
        recipient_email: archiveForm.recipient_email,
      });
      setArchiveMsg("Queued. Email when ready.");
    } catch (err) {
      setArchiveMsg(err.response?.data?.detail || err.message || "Request failed.");
    }
  };

  const results = data?.results || [];
  const displayResults = useMemo(
    () => results.filter((row) => rowMatchesQuickSearch(row, quickSearch)),
    [results, quickSearch]
  );

  const title = isAdmin ? "History" : INVESTOR_HISTORY_PAGE_TITLE;
  const subtitleDays = data?.ui_history_days ?? UI_FALLBACK_DAYS;
  const historyTabItems = isAdmin
    ? historySections.map((id) => ({ type: "section", id }))
    : getInvestorHistoryTabItems();
  const investorSubtitle = isHoldingsView
    ? "Current share positions · cost, NAV, and acquisition history"
    : `Your movements · ${subtitleDays}-day window`;
  const headerActions =
    !isHoldingsView && section !== "platform-fees" ? (
      <>
        <input
          type="search"
          className="history-dash__search"
          placeholder="Search…"
          value={quickSearch}
          onChange={(e) => setQuickSearch(e.target.value)}
          aria-label="Search table"
        />
        <button
          type="button"
          className="history-btn history-btn--ghost"
          onClick={() => onPdf().catch(() => {})}
          disabled={pdfBusy}
        >
          {pdfBusy ? "Preparing…" : "Export PDF"}
        </button>
      </>
    ) : null;

  return (
    <div className={`page history-dash${isAdmin ? "" : " investor-panel investor-panel-enhanced"}`}>
      {!isAdmin ? (
        <InvestorPageHeader icon="reports" title={title} subtitle={investorSubtitle} actions={headerActions} />
      ) : null}
      <div className="history-dash__main">
        {isAdmin ? (
          <header className="history-dash__header">
            <div>
              <h1 className="history-dash__title">{title}</h1>
              <p className="history-dash__meta">
                Movements · <strong>{subtitleDays}-day</strong> list{filters.archive ? " · full range" : ""}
              </p>
            </div>
            <div className="history-dash__header-actions">{headerActions}</div>
          </header>
        ) : null}

        <nav
          className="history-dash__tabs"
          aria-label={isAdmin ? "History sections" : "Transaction report sections"}
          role="tablist"
        >
          {historyTabItems.map((item) => {
            if (item.type === "link") {
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={false}
                  id={`history-tab-${item.id}`}
                  className="history-dash__tab"
                  onClick={() => navigate(item.to)}
                >
                  <HistoryTabLabel sectionId={item.id}>{item.label}</HistoryTabLabel>
                </button>
              );
            }
            const id = item.id;
            const m = SECTION_META[id];
            const label = isAdmin ? m.labelAdmin : m.labelInvestor;
            const active = id === section;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                id={`history-tab-${id}`}
                className={`history-dash__tab${active ? " history-dash__tab--active" : ""}`}
                onClick={() => navigate(`${basePath}/${id}`)}
              >
                {!isAdmin ? <HistoryTabLabel sectionId={id}>{label}</HistoryTabLabel> : label}
              </button>
            );
          })}
        </nav>

        {!isHoldingsView ? (
          <div className="history-dash__sticky">
            <SummaryCards summary={data?.summary} />
            {section === "platform-fees" && data?.revenue_account_email ? (
              <p className="muted" style={{ margin: "8px 0 0", fontSize: 13 }}>
                Cash fees accumulate in the company revenue wallet (
                <strong>{data.revenue_account_email}</strong>
                ). Share-component transfer fees are credited to the System Shareholder Account (SSA); project cap is unchanged.
              </p>
            ) : null}
          </div>
        ) : null}

        {isHoldingsView ? (
          holdingsLoading ? (
            <p className="muted">Loading holdings…</p>
          ) : (
            <MyPortfolioSection
              holdings={portfolio}
              redemptionBalances={redemptionBalances}
              formatShare={formatShare}
              formatMoney={(n) => bdtFull.format(n)}
              lifecyclePillClass={investorLifecyclePillClass}
            />
          )
        ) : (
          <>
        <section className="history-filters-card card">
          <div className="history-filters-card__bar">
            <button
              type="button"
              className="history-filters-card__toggle"
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
            >
              <span className="history-filters-card__toggle-icon" aria-hidden>
                {filtersOpen ? "▾" : "▸"}
              </span>
              Filters
            </button>
            <button
              type="button"
              className="history-btn history-btn--secondary"
              onClick={() => load().catch(() => {})}
              disabled={loading}
            >
              {loading ? "…" : "Apply"}
            </button>
          </div>
          {filtersOpen && (
            <div className="history-filters-grid">
              <label className="history-field">
                <span className="history-field__label">From</span>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value, page: 1 }))}
                />
              </label>
              <label className="history-field">
                <span className="history-field__label">To</span>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value, page: 1 }))}
                />
              </label>
              {meta.hasStatusFilter && (
                <label className="history-field">
                  <span className="history-field__label">Status</span>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))}
                  >
                    {meta.statusOptions.map((o) => (
                      <option key={o.value || "any"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {meta.hasProjectFilter && (
                <label className="history-field history-field--grow">
                  <span className="history-field__label">Project</span>
                  <select
                    value={filters.project_id}
                    onChange={(e) => setFilters((f) => ({ ...f, project_id: e.target.value, page: 1 }))}
                  >
                    <option value="">All projects</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {(p.project_code || "").toString()} {p.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {isAdmin && (
                <label className="history-field history-field--grow">
                  <span className="history-field__label">Investor</span>
                  <input
                    type="search"
                    placeholder="UUID or account number"
                    value={filters.investor_id}
                    onChange={(e) => setFilters((f) => ({ ...f, investor_id: e.target.value.trim(), page: 1 }))}
                  />
                </label>
              )}
              {meta.hasMovementFilter && (
                <label className="history-field history-field--grow">
                  <span className="history-field__label">Movement type</span>
                  <select
                    value={filters.movement_type}
                    onChange={(e) => setFilters((f) => ({ ...f, movement_type: e.target.value, page: 1 }))}
                  >
                    {MOVEMENT_OPTIONS.map((o) => (
                      <option key={o.value || "all"} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {isAdmin && (
                <label className="history-field history-field--checkbox">
                  <input
                    type="checkbox"
                    checked={filters.archive}
                    onChange={(e) => setFilters((f) => ({ ...f, archive: e.target.checked, page: 1 }))}
                  />
                  <span>Full archive (admin)</span>
                </label>
              )}
            </div>
          )}
        </section>

        {error && <div className="history-alert history-alert--error" role="alert">{error}</div>}

        <div className="history-table-card card">
          {loading && <div className="history-table-card__loading">Loading…</div>}
          <div className="history-table-scroll">
            <table className="history-data-table">
              <thead>
                <tr>
                  {columnKeys.map((col) => (
                    <th key={col}>{headerLabelsMap[col] || col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayResults.map((row, idx) => (
                  <tr
                    key={
                      row.transaction_id ||
                      row.deposit_id ||
                      row.withdrawal_id ||
                      row.transfer_id ||
                      row.redemption_id ||
                      `${section}-${row.sl}-${idx}`
                    }
                  >
                    {columnKeys.map((col) => {
                      let v = row[col];
                      if (col === "redemption_id") v = row.redemption_ref || row.redemption_id;
                      if (col === "status" || col === "remarks") {
                        return (
                          <td key={col}>
                            <span className={statusBadgeClass(v)}>{v}</span>
                          </td>
                        );
                      }
                      if (col === "transfer_fee_bdt" && row.transfer_fee_detail) {
                        return (
                          <td key={col} title={String(row.transfer_fee_detail)}>
                            {v == null || v === "" ? "—" : String(v)}
                          </td>
                        );
                      }
                      if (col === "shares" && row.shares_detail) {
                        return (
                          <td key={col} title={String(row.shares_detail)}>
                            {v == null || v === "" ? "—" : String(v)}
                          </td>
                        );
                      }
                      return <td key={col}>{v == null || v === "" ? "—" : String(v)}</td>;
                    })}
                  </tr>
                ))}
                {!displayResults.length && !loading && (
                  <tr>
                    <td colSpan={columnKeys.length} className="history-empty">
                      {quickSearch.trim() ? "No match." : "No data."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <footer className="history-pagination">
            <button
              type="button"
              className="history-btn history-btn--secondary"
              disabled={loading || filters.page <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: Math.max(1, f.page - 1) }))}
            >
              Previous
            </button>
            <span className="history-pagination__meta">
              Page {data?.page || filters.page} of{" "}
              {Math.max(1, Math.ceil((data?.total_count || 0) / (data?.page_size || filters.page_size || 25)))} ·{" "}
              {data?.total_count ?? 0} total
            </span>
            <button
              type="button"
              className="history-btn history-btn--secondary"
              disabled={
                loading ||
                !data ||
                (data.page || filters.page) * (data.page_size || filters.page_size || 25) >= (data.total_count || 0)
              }
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            >
              Next
            </button>
          </footer>
        </div>

        </>
        )}

        {isAdmin && (
          <section className="history-archive card">
            <button
              type="button"
              className="history-archive__toggle"
              onClick={() => setArchiveOpen((v) => !v)}
              aria-expanded={archiveOpen}
            >
              <span aria-hidden>{archiveOpen ? "▾" : "▸"}</span> Archive by email
            </button>
            {archiveOpen && (
              <>
                <p className="history-archive__hint">Any date range · queued</p>
                <form className="history-archive__form" onSubmit={submitArchive}>
                  <label className="history-field">
                    <span className="history-field__label">From</span>
                    <input
                      type="date"
                      required
                      value={archiveForm.date_from}
                      onChange={(e) => setArchiveForm((a) => ({ ...a, date_from: e.target.value }))}
                    />
                  </label>
                  <label className="history-field">
                    <span className="history-field__label">To</span>
                    <input
                      type="date"
                      required
                      value={archiveForm.date_to}
                      onChange={(e) => setArchiveForm((a) => ({ ...a, date_to: e.target.value }))}
                    />
                  </label>
                  <label className="history-field history-field--grow">
                    <span className="history-field__label">Email</span>
                    <input
                      type="email"
                      required
                      value={archiveForm.recipient_email}
                      onChange={(e) => setArchiveForm((a) => ({ ...a, recipient_email: e.target.value }))}
                    />
                  </label>
                  <button type="submit" className="history-btn history-btn--primary">
                    Request
                  </button>
                </form>
                {archiveMsg && <p className="history-archive__msg">{archiveMsg}</p>}
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
